import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";

export interface WorkspaceMember {
  id: string;
  name: string;
  email: string;
  role: string;
  joined_at?: string;
  status?: "active" | "pending";
}

/** Sprint 14a — a not-yet-accepted invitation, surfaced below the members table. */
export interface PendingInvitation {
  id: string;
  email: string;
  role: string;
  invited_by: string;
  created_at: string;
  expires_at: string;
}

export interface WorkspaceMembersData {
  members: WorkspaceMember[];
  pending_invitations: PendingInvitation[];
}

const MEMBERS_KEY = ["workspace-members"] as const;

/**
 * Sprint 14a — returns both active members and pending invitations. `list_members`
 * always returns both arrays; callers that only want active members read `.members`.
 */
export function useWorkspaceMembers() {
  return useQuery({
    queryKey: MEMBERS_KEY,
    queryFn: async (): Promise<WorkspaceMembersData> => {
      const result = await mcpClient.toolsCall<WorkspaceMembersData>("list_members");
      const payload = result.structuredContent as Partial<WorkspaceMembersData> | undefined;
      return {
        members: payload?.members ?? [],
        pending_invitations: payload?.pending_invitations ?? [],
      };
    },
  });
}

function errorText(result: { isError?: boolean; content?: unknown }, fallback: string): string {
  return Array.isArray(result.content)
    ? result.content.map((c: { text?: string }) => c.text).join("") || fallback
    : fallback;
}

export function useInviteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { email: string; role: string }): Promise<void> => {
      const result = await mcpClient.toolsCall("invite_user", {
        email: params.email,
        role: params.role,
      });
      if (result.isError) throw new Error(errorText(result, "Invitation failed"));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MEMBERS_KEY });
    },
  });
}

/** Sprint 14a — owner-only role change (owner ⇄ member). */
export function useUpdateMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { user_id: string; new_role: "owner" | "member" }): Promise<void> => {
      const result = await mcpClient.toolsCall("update_member_role", params);
      if (result.isError) throw new Error(errorText(result, "Failed to update role"));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MEMBERS_KEY });
    },
  });
}

/** Sprint 14a — owner-only resend (re-sends email + extends expiry to +7 days). */
export function useResendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string): Promise<void> => {
      const result = await mcpClient.toolsCall("resend_invitation", { invitation_id: invitationId });
      if (result.isError) throw new Error(errorText(result, "Failed to resend invitation"));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MEMBERS_KEY });
    },
  });
}

/** Sprint 14a — owner-only cancel of a pending invitation. */
export function useCancelInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string): Promise<void> => {
      const result = await mcpClient.toolsCall("cancel_invitation", { invitation_id: invitationId });
      if (result.isError) throw new Error(errorText(result, "Failed to cancel invitation"));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MEMBERS_KEY });
    },
  });
}
